import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import PropTypes from 'prop-types';
import SafeReactPaginate from '../../General/SafeReactPaginate';
import BigSpinner from '../../General/BigSpinner';
import BtnToggleFilter from '../../General/Filter/BtnToggleFilter';
import { fetchCategoriaApi, fetchCategoriasApi, fetchProductosPorProveedorApi, fetchStockSucursalesProveedorApi, fetchVentasPorProveedorApi } from '../../../api/axios';
import { fetchProveedoresFilter } from '../../../actions/sellersActions';
import { DropDownArrow, customStyle, filterSelectMenuPortalProps } from '../../../template/CustomStyle';

// Charts (ya disponible en el proyecto: Dashboard usa lo mismo)
import { Pie, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement, Tooltip, Legend,
    CategoryScale, LinearScale, BarElement, Title
} from 'chart.js';

ChartJS.register(
    ArcElement, Tooltip, Legend,
    CategoryScale, LinearScale, BarElement, Title
);

const PAGE_HEADING_STYLE = {
    background: 'linear-gradient(to right, #f8f9fa 0%, #fff 100%)',
    borderLeft: '4px solid var(--rojo-megatone, #c00)'
};

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const ACCESS_TIPO_USUARIO = [1, 2];
const SESSION_KEY = 'marketplace_session';
const DEFAULT_VENTAS_DIAS = 15;

const PIE_CHART_COLORS = [
    { bg: 'rgba(230, 52, 9, 0.35)', border: 'rgba(230, 52, 9, 0.85)' },
    { bg: 'rgba(34, 197, 94, 0.35)', border: 'rgba(34, 197, 94, 0.85)' },
    { bg: 'rgba(59, 130, 246, 0.35)', border: 'rgba(59, 130, 246, 0.85)' },
    { bg: 'rgba(245, 158, 11, 0.35)', border: 'rgba(245, 158, 11, 0.85)' },
    { bg: 'rgba(99, 102, 241, 0.35)', border: 'rgba(99, 102, 241, 0.85)' },
    { bg: 'rgba(14, 165, 233, 0.35)', border: 'rgba(14, 165, 233, 0.85)' },
    { bg: 'rgba(100, 116, 139, 0.35)', border: 'rgba(100, 116, 139, 0.75)' }
];

const firstValue = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

const asArray = (value) => {
    if (Array.isArray(value)) return value;
    return [];
};

const displayValue = (value) => {
    if (value === undefined || value === null || value === '') return '-';

    if (typeof value === 'object') {
        const codigo = firstValue(value.codigo, value.Codigo, value.id, value.Id);
        const descripcion = firstValue(
            value.descripcion,
            value.Descripcion,
            value.denominacion,
            value.Denominacion,
            value.nombre,
            value.Nombre,
            value.label
        );

        return [codigo, descripcion].filter((part) => part !== undefined && part !== null && part !== '').join(' - ') || '-';
    }

    return value;
};

const formatInteger = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return displayValue(value);
    return new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(number);
};

const formatCurrency = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return displayValue(value);
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(number);
};

const toLocalDateInput = (date) => {
    try {
        return new Date(date).toLocaleDateString('en-CA');
    } catch {
        return '';
    }
};

const buildDateTimeRange = ({ fechaDesde, fechaHasta }) => {
    const desde = String(fechaDesde || '').trim();
    const hasta = String(fechaHasta || '').trim();
    if (!desde || !hasta) return { FechaDesde: null, FechaHasta: null };

    const d = new Date(`${desde}T00:00:00`);
    const h = new Date(`${hasta}T23:59:59.999`);
    if (Number.isNaN(d.getTime()) || Number.isNaN(h.getTime())) return { FechaDesde: null, FechaHasta: null };
    return { FechaDesde: d.toISOString(), FechaHasta: h.toISOString() };
};

const getVentasProveedorBlock = (ventas) => {
    const proveedores = Array.isArray(ventas?.proveedores) ? ventas.proveedores : [];
    return proveedores[0] ?? {};
};

const getVentasProductos = (ventas, onlyConsigna = false) => {
    const proveedorVentas = getVentasProveedorBlock(ventas);
    const productos = Array.isArray(proveedorVentas?.productos) ? proveedorVentas.productos : [];
    if (!onlyConsigna) return productos;

    return productos.filter((p) => {
        const prod = p?.producto ?? p?.Producto ?? {};
        return normalizeBoolean(firstValue(prod?.consigna, prod?.Consigna)) === true;
    });
};

/** Mapa codigo (SKU) -> unidades vendidas en el rango (VentasPorProveedor). */
const buildUnidadesVendidasByCodigo = (ventas, onlyConsigna = false) => {
    const map = new Map();
    if (!ventas) return map;

    getVentasProductos(ventas, onlyConsigna).forEach((p) => {
        const prod = p?.producto ?? p?.Producto ?? {};
        const codigo = getKey(firstValue(prod?.codigo, prod?.Codigo));
        if (!codigo) return;

        const unidades = Number(firstValue(p?.unidadesVendidas, p?.UnidadesVendidas, 0));
        const add = Number.isFinite(unidades) ? unidades : 0;
        map.set(codigo, (map.get(codigo) || 0) + add);
    });

    return map;
};

const sumInteger = (values = []) => values.reduce((acc, v) => {
    const n = Number(v);
    return acc + (Number.isFinite(n) ? n : 0);
}, 0);

const normalizeBoolean = (value) => {
    if (value === undefined || value === null || value === '') return null;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;

    const normalized = String(value).trim().toLowerCase();
    if (['true', '1', 'si', 's', 'yes'].includes(normalized)) return true;
    if (['false', '0', 'no', 'n'].includes(normalized)) return false;

    return null;
};

const displayBoolean = (value) => {
    if (value === true) return 'Si';
    if (value === false) return 'No';
    return '-';
};

const normalizeSubfamilia = (rawSubfamilia) => displayValue(rawSubfamilia);

const getKey = (value) => {
    if (value === undefined || value === null || value === '') return '';
    return String(value);
};

const getSessionCodProv = () => {
    if (typeof window === 'undefined') return '';

    try {
        const session = window.sessionStorage.getItem(SESSION_KEY);
        if (!session) return '';

        const data = JSON.parse(session);
        return getKey(data?.codProv ?? data?.CodProv);
    } catch (error) {
        return '';
    }
};

const extractCategoriasArray = (response) => {
    const data = response?.data ?? response?.Data ?? response ?? {};
    const categorias =
        asArray(data?.elementos).length ? data.elementos :
            asArray(data?.Elementos).length ? data.Elementos :
                asArray(data?.categorias).length ? data.categorias :
                    asArray(data?.Categorias).length ? data.Categorias :
                        asArray(data?.content).length ? data.content :
                            asArray(data?.Content).length ? data.Content :
                                asArray(data).length ? data :
                                    [];

    if (categorias.length) return categorias;

    const categoria = firstValue(data?.subFamilia, data?.SubFamilia, data?.subfamilia, data?.Subfamilia, data?.categoria, data?.Categoria);
    if (categoria) return [categoria];

    const hasCategoriaData = firstValue(data?.id, data?.Id, data?.codigo, data?.Codigo, data?.descripcion, data?.Descripcion);
    return hasCategoriaData ? [data] : [];
};

const getCategoriaDescripcion = (categoria) => firstValue(
    categoria?.descripcion,
    categoria?.Descripcion,
    categoria?.denominacion,
    categoria?.Denominacion,
    categoria?.nombre,
    categoria?.Nombre
);

const buildSubfamiliasMap = (response) => extractCategoriasArray(response).reduce((acc, categoria) => {
    const id = firstValue(categoria?.id, categoria?.Id, categoria?.codigo, categoria?.Codigo);
    const descripcion = getCategoriaDescripcion(categoria);

    if (getKey(id) && descripcion) {
        acc[getKey(id)] = descripcion;
    }

    return acc;
}, {});

const normalizeStockResponse = (response, request, selectedProveedor) => {
    const data = response ?? {};
    const rawElementos =
        asArray(data?.elementos).length ? data.elementos :
            asArray(data?.Elementos).length ? data.Elementos :
                asArray(data?.content).length ? data.content :
                    asArray(data?.Content).length ? data.Content :
                        asArray(data);

    const elementos = rawElementos.map((item, index) => {
        const producto = item?.producto ?? item?.Producto ?? item?.product ?? item?.Product ?? item ?? {};
        const stock = item?.stockPorProducto ?? item?.StockPorProducto ?? item?.stock ?? item?.Stock ?? {};
        const rawSubfamilia = firstValue(
            item?.subfamilia,
            item?.Subfamilia,
            item?.subFamilia,
            item?.SubFamilia,
            item?.categoria,
            item?.Categoria,
            producto?.subfamilia,
            producto?.Subfamilia,
            producto?.subFamilia,
            producto?.SubFamilia,
            producto?.categoria,
            producto?.Categoria
        );
        const subfamiliaId = firstValue(
            rawSubfamilia?.id,
            rawSubfamilia?.Id,
            rawSubfamilia?.codigo,
            rawSubfamilia?.Codigo,
            producto?.idSubfamilia,
            producto?.IdSubfamilia,
            producto?.idSubFamilia,
            producto?.IdSubFamilia,
            item?.idSubfamilia,
            item?.IdSubfamilia,
            item?.idSubFamilia,
            item?.IdSubFamilia
        );
        const subfamiliaDescripcion = firstValue(
            rawSubfamilia?.descripcion,
            rawSubfamilia?.Descripcion,
            rawSubfamilia?.denominacion,
            rawSubfamilia?.Denominacion,
            rawSubfamilia?.nombre,
            rawSubfamilia?.Nombre,
            producto?.descripcionSubfamilia,
            producto?.DescripcionSubfamilia,
            producto?.descripcionSubFamilia,
            producto?.DescripcionSubFamilia,
            item?.descripcionSubfamilia,
            item?.DescripcionSubfamilia,
            item?.descripcionSubFamilia,
            item?.DescripcionSubFamilia
        );
        const consigna = normalizeBoolean(firstValue(
            item?.consigna,
            item?.Consigna,
            item?.esConsigna,
            item?.EsConsigna,
            producto?.consigna,
            producto?.Consigna,
            producto?.esConsigna,
            producto?.EsConsigna,
            stock?.consigna,
            stock?.Consigna,
            stock?.esConsigna,
            stock?.EsConsigna
        ));
        const ventasPeriodo = firstValue(
            item?.ventasPeriodo,
            item?.VentasPeriodo,
            item?.ventasDelPeriodo,
            item?.VentasDelPeriodo,
            item?.cantidadVentas,
            item?.CantidadVentas,
            producto?.ventasPeriodo,
            producto?.VentasPeriodo,
            producto?.ventasDelPeriodo,
            producto?.VentasDelPeriodo,
            stock?.ventasPeriodo,
            stock?.VentasPeriodo
        );

        return {
            id: firstValue(producto?.id, producto?.Id, item?.id, item?.Id, index),
            sku: firstValue(producto?.codigo, producto?.Codigo, producto?.sku, producto?.SKU, item?.sku, item?.SKU),
            descripcion: firstValue(producto?.descripcion, producto?.Descripcion, item?.descripcion, item?.Descripcion),
            codigoSap: firstValue(producto?.codigoSap, producto?.CodigoSap, producto?.codigoSAP, producto?.CodigoSAP, item?.codigoSap, item?.CodigoSap),
            subfamiliaId,
            subfamilia: normalizeSubfamilia(firstValue(subfamiliaDescripcion, rawSubfamilia, subfamiliaId)),
            consigna,
            stockTotal: firstValue(
                stock?.disponibleSucursales,
                stock?.DisponibleSucursales,
                item?.disponibleSucursales,
                item?.DisponibleSucursales,
                stock?.disponibleSucursal,
                stock?.DisponibleSucursal,
                item?.disponibleSucursal,
                item?.DisponibleSucursal
            ),
            ventasPeriodo
        };
    });

    const proveedor = data?.proveedor ?? data?.Proveedor ?? {};
    const cantidadPagina = Number(firstValue(data?.cantidadPagina, data?.CantidadPagina, data?.pageSize, data?.PageSize, request?.CantidadPagina, 10));
    const cantidadTotal = Number(firstValue(data?.cantidadTotal, data?.CantidadTotal, data?.total, data?.Total, elementos.length));

    return {
        numeroPagina: Number(firstValue(data?.numeroPagina, data?.NumeroPagina, data?.page, data?.Page, request?.NumeroPagina, 1)),
        cantidadPagina,
        cantidadTotal,
        totalPaginas: Number(firstValue(data?.totalPaginas, data?.TotalPaginas, data?.totalPages, data?.TotalPages, cantidadPagina > 0 ? Math.ceil(cantidadTotal / cantidadPagina) : 0)),
        proveedor: {
            codigo: firstValue(proveedor?.codigo, proveedor?.Codigo, selectedProveedor?.value, request?.CodigoProveedor),
            denominacion: firstValue(proveedor?.denominacion, proveedor?.Denominacion, proveedor?.descripcion, proveedor?.Descripcion, selectedProveedor?.label),
            cuit: firstValue(proveedor?.cuit, proveedor?.Cuit),
            id: firstValue(proveedor?.id, proveedor?.Id)
        },
        elementos
    };
};

const parseJsonIfNeeded = (value) => {
    if (typeof value !== 'string') return value;

    const trimmed = value.trim();
    if (!trimmed) return {};

    try {
        return JSON.parse(trimmed);
    } catch {
        return value;
    }
};

const extractSucursalesStockArray = (value) => {
    const data = parseJsonIfNeeded(value);
    if (Array.isArray(data)) return data;
    if (!data || typeof data !== 'object') return null;

    const candidates = [
        data?.sucursalesStock,
        data?.SucursalesStock,
        data?.stockSucursales,
        data?.StockSucursales,
        data?.sucursales,
        data?.Sucursales
    ];

    const arrayCandidate = candidates.find(Array.isArray);
    if (arrayCandidate) return arrayCandidate;

    const hasSucursalData = firstValue(
        data?.nombreSucursal,
        data?.NombreSucursal,
        data?.numeroSucursal,
        data?.NumeroSucursal,
        data?.disponibleSucursal,
        data?.DisponibleSucursal
    );

    return hasSucursalData ? [data] : null;
};

const normalizeStockSucursalesResponse = (response) => {
    const root = parseJsonIfNeeded(response?.data ?? response?.Data ?? response ?? {});
    const candidates = [
        root,
        root?.data,
        root?.Data,
        root?.result,
        root?.Result,
        root?.resultado,
        root?.Resultado,
        root?.content,
        root?.Content
    ].map(parseJsonIfNeeded).filter((value) => value !== undefined && value !== null);

    const rawSucursales = candidates
        .map(extractSucursalesStockArray)
        .find((items) => Array.isArray(items)) ?? [];

    const messageSource = candidates.find((item) => item && typeof item === 'object' && firstValue(item?.mensaje, item?.Mensaje));

    return {
        mensaje: firstValue(messageSource?.mensaje, messageSource?.Mensaje, ''),
        sucursalesStock: rawSucursales.map((item, index) => ({
            id: firstValue(item?.id, item?.Id, item?.numeroSucursal, item?.NumeroSucursal, index),
            nombreSucursal: firstValue(item?.nombreSucursal, item?.NombreSucursal),
            numeroSucursal: firstValue(item?.numeroSucursal, item?.NumeroSucursal),
            depositoVenta: firstValue(item?.depositoVenta, item?.DepositoVenta),
            disponibleSucursal: firstValue(item?.disponibleSucursal, item?.DisponibleSucursal),
            pendienteFacturarSucursal: firstValue(item?.pendienteFacturarSucursal, item?.PendienteFacturarSucursal),
            pendienteIngresoSucursal: firstValue(item?.pendienteIngresoSucursal, item?.PendienteIngresoSucursal),
            reservadoCanalDigitalSucursal: firstValue(item?.reservadoCanalDigitalSucursal, item?.ReservadoCanalDigitalSucursal)
        }))
    };
};

const extractErrorMessage = (error) => {
    const data = error?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    return data?.mensaje ?? data?.Mensaje ?? data?.title ?? 'No se pudo consultar el stock del proveedor.';
};

class ControlStockProveedores extends Component {
    constructor(props) {
        super(props);
        this.stockSucursalesRequestId = 0;
        this.state = {
            selectedProveedor: null,
            codigoProveedor: '',
            cuit: '',
            producto: '',
            depositoStock: '',
            fechaDesde: toLocalDateInput(new Date(Date.now() - DEFAULT_VENTAS_DIAS * 24 * 60 * 60 * 1000)),
            fechaHasta: toLocalDateInput(new Date()),
            ventas: null,
            loadingVentas: false,
            loadingStock: false,
            loadingSubfamilias: false,
            subfamiliasLoaded: false,
            subfamiliasById: {},
            error: '',
            stock: null,
            onFilter: true,
            stockSort: null, // 'asc' | 'desc' | null
            globalSorted: false,
            stockSucursalesModal: {
                show: false,
                loading: false,
                error: '',
                producto: null,
                sucursalesStock: [],
                mensaje: ''
            },
            request: {
                NumeroPagina: 1,
                CantidadPagina: 10
            }
        };
    }

    componentDidMount() {
        if (!this.canAccess()) return;

        if (this.isProveedorUser()) {
            this.fetchProveedorStock();
        } else {
            this.props.fetchProveedoresFilter({ descripcion: '' });
        }
    }

    componentDidUpdate(prevProps) {
        if (!this.canAccess()) return;

        const becameProveedor = this.isProveedorUser() && !this.isProveedorUser(prevProps);
        const codProvCambio = this.isProveedorUser()
            && this.getProveedorCodigo() !== this.getProveedorCodigo(prevProps)
            && Boolean(this.getProveedorCodigo());

        if (becameProveedor || codProvCambio) {
            this.fetchProveedorStock();
        }
    }

    isProveedorUser = (props = this.props) => Boolean(props.userType?.isProveedor || Number(props.tipoUsuario) === 7)

    shouldShowConsignaColumn = () => !this.isProveedorUser()

    isClientPaged = () => Boolean(this.state.stock?.elementos?.length)

    getProveedorCodigo = (props = this.props) => getKey(props.codProv) || getSessionCodProv()

    fetchProveedorStock = () => {
        const codigoProveedor = this.getProveedorCodigo();

        if (!codigoProveedor) {
            this.setState({
                stock: null,
                error: 'No se puede determinar el codigo de proveedor. Cierre sesion y vuelva a ingresar.'
            });
            return;
        }

        this.fetchStock(1, this.state.request.CantidadPagina);
    }

    canAccess = () => {
        const { userType, tipoUsuario } = this.props;
        return Boolean(
            userType?.isCategory ||
            userType?.isAdminMod ||
            ACCESS_TIPO_USUARIO.includes(Number(tipoUsuario))
        );
    }

    mapProveedorOptions = () => {
        const listaProveedor = [];
        if (this.props.proveedores != undefined) {
            this.props.proveedores.forEach((item) => {
                listaProveedor.push({ label: item.label, value: item.value.toString() });
            });
        }
        return listaProveedor;
    }

    onSelectSearchInputProveedores = (descripcion) => {
        this.props.fetchProveedoresFilter({ descripcion });
    }

    mapProveedores = () => {
        this.props.fetchProveedoresFilter({ descripcion: null });
    }

    buildRequest = (numeroPagina, cantidadPagina) => {
        const request = {
            NumeroPagina: numeroPagina,
            CantidadPagina: cantidadPagina,
            numeroPagina,
            cantidadPagina
        };

        const producto = String(this.state.producto || '').trim();
        const depositoStockRaw = String(this.state.depositoStock || '').trim();
        const depositoStock = depositoStockRaw === '' ? null : Number(depositoStockRaw);

        if (producto) {
            request.Producto = producto;
        }

        if (depositoStockRaw !== '' && Number.isFinite(depositoStock)) {
            request.DepositoStock = depositoStock;
        }

        if (this.isProveedorUser()) {
            const codigoProveedor = this.getProveedorCodigo();

            if (codigoProveedor) {
                request.CodigoProveedor = codigoProveedor;
            }
        } else {
            const codigoProveedor = String(this.state.codigoProveedor || this.state.selectedProveedor?.value || '').trim();
            const cuit = String(this.state.cuit || '').trim();

            if (codigoProveedor) {
                request.CodigoProveedor = codigoProveedor;
            }

            if (cuit) {
                request.Cuit = cuit;
            }
        }

        return request;
    }

    fetchStock = (numeroPagina = 1, cantidadPagina = this.state.request.CantidadPagina) => {
        const request = this.buildRequest(numeroPagina, cantidadPagina);

        if (this.isProveedorUser() && !request.CodigoProveedor) {
            this.setState({
                stock: null,
                error: 'No se puede determinar el codigo de proveedor. Cierre sesion y vuelva a ingresar.'
            });
            return;
        }

        if (!request.CodigoProveedor && !request.Cuit && !this.isProveedorUser()) {
            this.setState({ stock: null, error: '' });
            return;
        }

        this.setState({ loadingStock: true, loadingVentas: true, error: '', request, stockSort: null, globalSorted: false });

        const stockRequest = this.fetchStockAllPagesMerged();

        stockRequest
            .then((stock) => {
                this.setState({
                    stock,
                    loadingStock: false
                }, () => {
                    this.loadSubfamiliasDescriptions(stock.elementos);
                    this.fetchVentasForCurrentProveedor(stock?.proveedor);
                });
            })
            .catch((error) => {
                this.setState({
                    error: extractErrorMessage(error),
                    stock: null,
                    loadingStock: false,
                    loadingVentas: false,
                    ventas: null
                });
            });
    }

    getCodProvForVentas = (proveedorFromStock) => {
        const fromSession = this.getProveedorCodigo();
        const fromStock = firstValue(proveedorFromStock?.codigo, proveedorFromStock?.Codigo);
        const fromSelected = firstValue(this.state.selectedProveedor?.value, this.state.codigoProveedor);
        const cod = this.isProveedorUser()
            ? firstValue(fromSession, fromStock)
            : firstValue(fromStock, fromSelected, fromSession);
        const n = Number(cod);
        return Number.isFinite(n) && n > 0 ? n : null;
    }

    filterElementosForRole = (elementos = []) => {
        if (!this.isProveedorUser()) return elementos;
        return elementos.filter((item) => item?.consigna === true);
    }

    fetchVentasForCurrentProveedor = (proveedorFromStock) => {
        const codProv = this.getCodProvForVentas(proveedorFromStock);
        const { FechaDesde, FechaHasta } = buildDateTimeRange({
            fechaDesde: this.state.fechaDesde,
            fechaHasta: this.state.fechaHasta
        });

        if (!codProv || !FechaDesde || !FechaHasta) {
            this.setState({ ventas: null, loadingVentas: false });
            return;
        }

        fetchVentasPorProveedorApi({ CodProv: codProv, FechaDesde, FechaHasta })
            .then((data) => {
                this.setState({ ventas: data ?? null, loadingVentas: false });
            })
            .catch(() => {
                this.setState({ ventas: null, loadingVentas: false });
            });
    }

    /** Trae todas las páginas del backend para gráficos/resumen sobre el total absoluto. */
    fetchStockAllPagesMerged = async (sortDirection = null) => {
        const cantidadPagina = Number(this.state.request.CantidadPagina ?? 10);
        const requestBase = this.buildRequest(1, cantidadPagina);
        const firstRequest = {
            ...requestBase,
            NumeroPagina: 1,
            numeroPagina: 1
        };

        const firstResponse = await fetchProductosPorProveedorApi(firstRequest);
        const firstStock = normalizeStockResponse(firstResponse, firstRequest, this.state.selectedProveedor);
        const totalPaginasBackend = Number(firstStock.totalPaginas || 1);

        let elementos = [...(firstStock.elementos ?? [])];

        if (totalPaginasBackend > 1) {
            const pageRequests = [];
            for (let page = 2; page <= totalPaginasBackend; page += 1) {
                const pageRequest = {
                    ...requestBase,
                    NumeroPagina: page,
                    numeroPagina: page
                };
                pageRequests.push(
                    fetchProductosPorProveedorApi(pageRequest)
                        .then((resp) => normalizeStockResponse(resp, pageRequest, this.state.selectedProveedor))
                        .then((stock) => stock.elementos ?? [])
                        .catch(() => [])
                );
            }

            const restElementos = await Promise.all(pageRequests);
            elementos = [...elementos, ...restElementos.flat()];
        }

        elementos = this.filterElementosForRole(elementos);

        if (sortDirection) {
            const dir = sortDirection === 'asc' ? 1 : -1;
            const asNumber = (value) => {
                const n = Number(value);
                return Number.isFinite(n) ? n : 0;
            };
            elementos.sort((a, b) => dir * (asNumber(a?.stockTotal) - asNumber(b?.stockTotal)));
        }

        return {
            ...firstStock,
            elementos,
            numeroPagina: 1,
            cantidadPagina,
            cantidadTotal: elementos.length,
            totalPaginas: cantidadPagina > 0 ? Math.ceil(elementos.length / cantidadPagina) : 1
        };
    }

    loadSubfamiliasDescriptions = (elementos = []) => {
        const ids = [...new Set(elementos.map((item) => getKey(item?.subfamiliaId)).filter(Boolean))];
        const missingIds = ids.filter((id) => !this.state.subfamiliasById[id]);

        if (!missingIds.length || this.state.loadingSubfamilias) return;

        this.setState({ loadingSubfamilias: true });

        const catalogPromise = this.state.subfamiliasLoaded
            ? Promise.resolve({})
            : fetchCategoriasApi('')
                .then((response) => buildSubfamiliasMap(response))
                .catch(() => ({}));

        catalogPromise
            .then((subfamiliasById) => {
                const unresolvedIds = missingIds.filter((id) => !subfamiliasById[id]);

                if (!unresolvedIds.length) {
                    return subfamiliasById;
                }

                return Promise.all(unresolvedIds.map((id) => (
                    fetchCategoriaApi(id)
                        .then((categoriaResponse) => {
                            const resolvedById = buildSubfamiliasMap(categoriaResponse);
                            const descripcion = resolvedById[id] ?? getCategoriaDescripcion(extractCategoriasArray(categoriaResponse)[0]) ?? categoriaResponse?.descripcion ?? categoriaResponse?.Descripcion;

                            return descripcion ? { [id]: descripcion } : {};
                        })
                        .catch(() => ({}))
                )))
                    .then((resolvedResults) => resolvedResults.reduce((acc, resolved) => ({
                        ...acc,
                        ...resolved
                    }), subfamiliasById));
            })
            .then((subfamiliasById) => {
                this.setState((prevState) => ({
                    subfamiliasById: {
                        ...prevState.subfamiliasById,
                        ...subfamiliasById
                    },
                    loadingSubfamilias: false,
                    subfamiliasLoaded: true
                }));
            })
            .catch(() => {
                this.setState({
                    loadingSubfamilias: false,
                    subfamiliasLoaded: true
                });
            });
    }

    handleProveedorChange = (selectedProveedor) => {
        this.setState({
            selectedProveedor,
            codigoProveedor: selectedProveedor?.value ? String(selectedProveedor.value) : ''
        }, () => {
            if (selectedProveedor?.value || this.state.cuit) {
                this.fetchStock(1, this.state.request.CantidadPagina);
            } else {
                this.setState({ stock: null, error: '' });
            }
        });
    }

    handleFilterInputChange = (event) => {
        const { name, value } = event.target;
        this.setState({ [name]: value });
    }

    clearFilters = () => {
        const defaultFechas = {
            fechaDesde: toLocalDateInput(new Date(Date.now() - DEFAULT_VENTAS_DIAS * 24 * 60 * 60 * 1000)),
            fechaHasta: toLocalDateInput(new Date()),
        };

        if (this.isProveedorUser()) {
            this.setState({
                ...defaultFechas,
                ventas: null,
                error: ''
            }, () => {
                if (this.getProveedorCodigo()) {
                    this.fetchProveedorStock();
                }
            });
            return;
        }

        this.setState({
            selectedProveedor: null,
            codigoProveedor: '',
            cuit: '',
            producto: '',
            depositoStock: '',
            ...defaultFechas,
            ventas: null,
            stock: null,
            error: ''
        });
        this.props.fetchProveedoresFilter({ descripcion: '' });
    }

    handlePageClick = (event) => {
        const numeroPagina = event.selected + 1;
        if (!this.state.stock) return;

        this.setState((prev) => ({
            stock: {
                ...prev.stock,
                numeroPagina,
            }
        }));
    }

    handlePageSizeChange = (event) => {
        const cantidadPagina = Number(event.target.value);
        if (!this.state.stock) {
            this.setState({ request: { ...this.state.request, CantidadPagina: cantidadPagina } });
            return;
        }

        const total = this.getVisibleElementos().length;
        this.setState((prev) => ({
            request: { ...prev.request, CantidadPagina: cantidadPagina },
            stock: {
                ...prev.stock,
                cantidadPagina,
                numeroPagina: 1,
                totalPaginas: cantidadPagina > 0 ? Math.ceil(total / cantidadPagina) : 1,
            }
        }));
    }

    toggleStockSort = () => {
        const next =
            this.state.stockSort === 'asc'
                ? 'desc'
                : this.state.stockSort === 'desc'
                    ? null
                    : 'asc';

        this.setState({
            stockSort: next,
            globalSorted: Boolean(next),
            stock: this.state.stock
                ? { ...this.state.stock, numeroPagina: 1 }
                : this.state.stock
        });
    }

    getConsignaFamiliaKeys = (elementos = []) => {
        const keys = new Set();
        elementos.forEach((item) => {
            if (item?.consigna === true) {
                const subfamiliaKey = getKey(item?.subfamiliaId);
                if (subfamiliaKey) keys.add(subfamiliaKey);
            }
        });
        return keys;
    }

    sortElementosByStock = (elementos = []) => {
        const sort = this.state.stockSort;
        if (!sort) return elementos;

        const dir = sort === 'asc' ? 1 : -1;
        const asNumber = (value) => {
            const n = Number(value);
            return Number.isFinite(n) ? n : 0;
        };

        return [...elementos].sort((a, b) => dir * (asNumber(a?.stockTotal) - asNumber(b?.stockTotal)));
    }

    renderSubfamilia = (item) => {
        const subfamiliaId = getKey(item?.subfamiliaId);
        const descripcion = subfamiliaId ? this.state.subfamiliasById[subfamiliaId] : '';

        if (subfamiliaId && descripcion) return `${subfamiliaId} - ${descripcion}`;
        if (descripcion) return descripcion;

        return displayValue(item?.subfamilia);
    }

    renderConsigna = (item) => {
        const className = item?.consigna === true ? 'badge bg-success' : item?.consigna === false ? 'badge bg-secondary' : 'text-muted';
        return <span className={className}>{displayBoolean(item?.consigna)}</span>;
    }

    renderStockTotal = (item) => {
        if (this.isProveedorUser() && item?.consigna !== true) return '';
        return formatInteger(item?.stockTotal);
    }

    openStockSucursalesModal = (item) => {
        const codigo = getKey(item?.sku).trim();
        const requestId = this.stockSucursalesRequestId + 1;
        this.stockSucursalesRequestId = requestId;

        this.setState({
            stockSucursalesModal: {
                show: true,
                loading: true,
                error: '',
                producto: item,
                sucursalesStock: [],
                mensaje: ''
            }
        });

        if (!codigo) {
            this.setState((prevState) => ({
                stockSucursalesModal: {
                    ...prevState.stockSucursalesModal,
                    loading: false,
                    error: 'No se pudo determinar el SKU del producto.'
                }
            }));
            return;
        }

        fetchStockSucursalesProveedorApi({ codigo })
            .then((response) => {
                if (requestId !== this.stockSucursalesRequestId) return;

                const detalle = normalizeStockSucursalesResponse(response);
                this.setState((prevState) => ({
                    stockSucursalesModal: {
                        ...prevState.stockSucursalesModal,
                        loading: false,
                        error: '',
                        sucursalesStock: detalle.sucursalesStock,
                        mensaje: detalle.mensaje
                    }
                }));
            })
            .catch((error) => {
                if (requestId !== this.stockSucursalesRequestId) return;

                this.setState((prevState) => ({
                    stockSucursalesModal: {
                        ...prevState.stockSucursalesModal,
                        loading: false,
                        error: extractErrorMessage(error),
                        sucursalesStock: []
                    }
                }));
            });
    }

    closeStockSucursalesModal = () => {
        this.stockSucursalesRequestId += 1;
        this.setState((prevState) => ({
            stockSucursalesModal: {
                ...prevState.stockSucursalesModal,
                show: false,
                loading: false
            }
        }));
    }

    renderStockActualCell = (item) => {
        const codigo = getKey(item?.sku);

        return (
            <div className="stock-actual-cell">
                <span>{this.renderStockTotal(item)}</span>
                <button
                    type="button"
                    className="btn btn-link p-0 stock-detail-btn"
                    onClick={() => this.openStockSucursalesModal(item)}
                    disabled={!codigo}
                    title="Ver stock por sucursal"
                    aria-label={`Ver stock por sucursal ${displayValue(item?.sku)}`}
                >
                    <i className="fa fa-eye" aria-hidden="true" />
                </button>
            </div>
        );
    }

    getVisibleElementos = () => this.filterElementosForRole(this.state.stock?.elementos ?? [])

    /** Dataset completo para gráficos y totales (todas las páginas, sin slice de tabla). */
    getElementosParaAnalitica = () => this.sortElementosByStock(this.getVisibleElementos())

    /** Stock del resumen/gráfico circular: solo consignación (misma base admin y proveedor). */
    getElementosParaStockResumen = () => this.getElementosParaAnalitica().filter((item) => item?.consigna === true)

    getUnidadesVendidasByCodigo = () => buildUnidadesVendidasByCodigo(
        this.state.ventas,
        this.isProveedorUser()
    )

    getUnidadesVendidasForItem = (item) => {
        const map = this.getUnidadesVendidasByCodigo();
        const keys = [
            getKey(item?.sku),
            getKey(item?.codigoSap)
        ].filter(Boolean);

        for (let i = 0; i < keys.length; i += 1) {
            if (map.has(keys[i])) {
                return map.get(keys[i]);
            }
        }

        return this.state.ventas && !this.state.loadingVentas ? 0 : null;
    }

    renderUnidadesVendidasCell = (item) => {
        if (this.state.loadingVentas) {
            return <span className="text-muted small">...</span>;
        }

        if (!this.state.ventas) {
            return '-';
        }

        const unidades = this.getUnidadesVendidasForItem(item);
        if (unidades === null || unidades === undefined) {
            return '-';
        }

        return formatInteger(unidades);
    }

    resolveSubfamiliaChartLabel = (key) => {
        if (key === 'Otros') return 'Otros subfamilias';
        const id = getKey(key);
        const descripcion = id ? this.state.subfamiliasById[id] : '';
        if (id && descripcion) return descripcion;
        if (descripcion) return descripcion;
        return key || 'Sin subfamilia';
    }

    buildChartsData = (elementos = []) => {
        const items = Array.isArray(elementos) ? elementos : [];
        if (!items.length) return { pieData: null, pieLegend: [] };

        const bySubfamilia = new Map();
        items.forEach((it) => {
            const key = getKey(it?.subfamiliaId) || String(it?.subfamilia || 'Sin subfamilia');
            const stock = Number(it?.stockTotal);
            const value = Number.isFinite(stock) ? stock : 0;
            bySubfamilia.set(key, (bySubfamilia.get(key) || 0) + value);
        });

        const subfamiliasSorted = [...bySubfamilia.entries()].sort((a, b) => b[1] - a[1]);
        const top = subfamiliasSorted.slice(0, 6);
        const otros = subfamiliasSorted.slice(6).reduce((acc, [, v]) => acc + v, 0);

        const slices = [
            ...top.map(([key, stock]) => ({ key, stock })),
            ...(otros > 0 ? [{ key: 'Otros', stock: otros }] : [])
        ];

        const pieLegend = slices.map((slice, index) => {
            const color = PIE_CHART_COLORS[index % PIE_CHART_COLORS.length];
            return {
                key: slice.key,
                label: this.resolveSubfamiliaChartLabel(slice.key),
                stock: slice.stock,
                color: color.bg,
                borderColor: color.border
            };
        });

        const pieData = {
            labels: pieLegend.map((x) => x.label),
            datasets: [{
                data: pieLegend.map((x) => x.stock),
                backgroundColor: pieLegend.map((x) => x.color),
                borderColor: pieLegend.map((x) => x.borderColor),
                borderWidth: 1,
            }]
        };

        return { pieData, pieLegend };
    }

    renderPieChartLegend = (pieLegend = []) => {
        if (!pieLegend.length) return null;

        return (
            <ul className="admin-stock-pie-legend list-unstyled mb-0">
                {pieLegend.map((item) => (
                    <li key={item.key} className="admin-stock-pie-legend__item">
                        <span
                            className="admin-stock-pie-legend__swatch"
                            style={{ backgroundColor: item.color, borderColor: item.borderColor }}
                            aria-hidden="true"
                        />
                        <span className="admin-stock-pie-legend__text">
                            <span className="admin-stock-pie-legend__label">{item.label}</span>
                            <span className="admin-stock-pie-legend__value">{formatInteger(item.stock)} u.</span>
                        </span>
                    </li>
                ))}
            </ul>
        );
    }

    buildVentasBarData = () => {
        const productosVentas = getVentasProductos(this.state.ventas, this.isProveedorUser());
        if (!productosVentas.length) return null;

        const topVentas = [...productosVentas]
            .map((p) => {
                const prod = p?.producto ?? p?.Producto ?? {};
                const sku = firstValue(prod?.codigo, prod?.Codigo);
                const unidades = Number(firstValue(p?.unidadesVendidas, p?.UnidadesVendidas, 0));
                return {
                    label: String(sku || '').trim() || 'SKU',
                    unidades: Number.isFinite(unidades) ? unidades : 0
                };
            })
            .sort((a, b) => b.unidades - a.unidades)
            .slice(0, 10);

        if (!topVentas.length) return null;

        return {
            labels: topVentas.map((x) => x.label),
            datasets: [{
                label: 'Unidades vendidas',
                data: topVentas.map((x) => x.unidades),
                backgroundColor: 'rgba(59, 130, 246, 0.28)',
                borderColor: 'rgba(59, 130, 246, 0.75)',
                borderWidth: 1,
                borderRadius: 8,
            }]
        };
    }

    renderResumenVentasYGraficos() {
        const elementos = this.getElementosParaAnalitica();
        const elementosStock = this.getElementosParaStockResumen();
        if (!elementos.length || this.state.loadingStock) return null;

        const totalStock = sumInteger(elementosStock.map((x) => x?.stockTotal));
        const consignacionCount = elementosStock.length;
        const { pieData, pieLegend } = this.buildChartsData(elementosStock);

        const ventas = this.state.ventas ?? {};
        const proveedorVentas = getVentasProveedorBlock(ventas);

        const totalUnidadesVendidas = firstValue(
            proveedorVentas?.totalUnidadesVendidas,
            proveedorVentas?.TotalUnidadesVendidas,
            ventas?.totalUnidadesVendidas,
            ventas?.TotalUnidadesVendidas,
            0
        );
        const importeTotalVenta = firstValue(
            proveedorVentas?.importeTotalVenta,
            proveedorVentas?.ImporteTotalVenta,
            ventas?.importeTotalVenta,
            ventas?.ImporteTotalVenta,
            0
        );

        const barData = this.buildVentasBarData();
        const fechaDesdeLabel = this.state.fechaDesde || '-';
        const fechaHastaLabel = this.state.fechaHasta || '-';

        return (
            <div className="admin-stock-report mb-3">
                <div className="admin-stock-report__header">
                    <div className="admin-stock-report__title">
                        <i className="fa fa-chart-bar" aria-hidden="true" /> Resumen de ventas ({fechaDesdeLabel} — {fechaHastaLabel})
                    </div>
                    <div className="admin-stock-report__pills">
                        <span className="admin-metric-pill">
                            <i className="fa fa-cubes" aria-hidden="true" /> Stock {formatInteger(totalStock)}
                        </span>
                        <span className="admin-metric-pill">
                            <i className="fa fa-shopping-cart" aria-hidden="true" /> Unidades {formatInteger(totalUnidadesVendidas)}
                        </span>
                        <span className="admin-metric-pill">
                            <i className="fa fa-money-bill-wave" aria-hidden="true" /> Importe {formatCurrency(importeTotalVenta)}
                        </span>
                        {!this.isProveedorUser() && (
                            <span className="admin-metric-pill">
                                <i className="fa fa-check-circle" aria-hidden="true" /> En consignación {formatInteger(consignacionCount)}
                            </span>
                        )}
                    </div>
                </div>

                <div className="row g-3">
                    <div className="col-12 col-lg-5">
                        <div className="admin-stock-chart-card">
                            <div className="admin-stock-chart-card__title">
                                Distribución de stock por subfamilia
                                <span className="admin-stock-chart-card__subtitle">({formatInteger(elementosStock.length)} productos en consignación)</span>
                            </div>
                            <div className="admin-stock-chart-card__body row g-0 align-items-center">
                                <div className="col-7">
                                    <div style={{ position: 'relative', height: '260px' }}>
                                        {pieData ? (
                                            <Pie
                                                data={pieData}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    plugins: { legend: { display: false } }
                                                }}
                                            />
                                        ) : null}
                                    </div>
                                </div>
                                <div className="col-5 admin-stock-chart-card__legend-col">
                                    {this.renderPieChartLegend(pieLegend)}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-lg-7">
                        <div className="admin-stock-chart-card">
                            <div className="admin-stock-chart-card__title">Top productos por ventas del período</div>
                            <div style={{ position: 'relative', height: '280px' }}>
                                {barData ? (
                                    <Bar
                                        data={barData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: { legend: { display: false } },
                                            scales: {
                                                x: { ticks: { maxRotation: 0, minRotation: 0 } },
                                                y: { beginAtZero: true }
                                            }
                                        }}
                                    />
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderProveedorHeader() {
        const proveedor = this.state.stock?.proveedor;
        if (!proveedor) return null;

        const denominacion = displayValue(proveedor.denominacion);
        const codigo = displayValue(proveedor.codigo);
        const cuit = displayValue(proveedor.cuit);
        const id = displayValue(proveedor.id);

        const campos = [
            {
                key: 'codigo',
                label: 'Cod. proveedor',
                icon: 'fa fa-hashtag',
                value: codigo,
                modifier: 'codigo',
                highlight: true
            },
            {
                key: 'denominacion',
                label: 'Razon social',
                icon: 'fa fa-building',
                value: denominacion,
                modifier: 'denominacion',
                highlight: false
            },
            {
                key: 'cuit',
                label: 'CUIT',
                icon: 'fa fa-id-card',
                value: cuit,
                modifier: 'cuit',
                highlight: false
            },
            {
                key: 'id',
                label: 'ID interno',
                icon: 'fa fa-barcode',
                value: id,
                modifier: 'id',
                highlight: false
            }
        ];

        return (
            <section className="admin-entity-summary mb-3" aria-label="Datos del proveedor consultado">
                <header className="admin-entity-summary__header">
                    <div className="admin-entity-summary__header-main">
                        <span className="admin-entity-summary__icon" aria-hidden="true">
                            <i className="fa fa-truck" />
                        </span>
                        <div>
                            <span className="admin-entity-summary__eyebrow">Proveedor consultado</span>
                            <h2 className="admin-entity-summary__title">{denominacion}</h2>
                        </div>
                    </div>
                    {codigo !== '-' && (
                        <span className="admin-metric-pill">
                            <i className="fa fa-hashtag" aria-hidden="true" />
                            Cod. {codigo}
                        </span>
                    )}
                </header>

                <div className="admin-entity-summary__grid">
                    {campos.map((campo) => (
                        <div
                            key={campo.key}
                            className={`admin-entity-summary__cell admin-entity-summary__cell--${campo.modifier}`}
                        >
                            <span className="admin-entity-summary__cell-label">
                                <i className={campo.icon} aria-hidden="true" />
                                {campo.label}
                            </span>
                            <span
                                className={
                                    campo.highlight
                                        ? 'admin-entity-summary__cell-value admin-entity-summary__cell-value--highlight'
                                        : 'admin-entity-summary__cell-value'
                                }
                            >
                                {campo.value}
                            </span>
                        </div>
                    ))}
                </div>
            </section>
        );
    }

    renderPageHeading(showFilterToggle) {
        const cantidadTotal = this.state.stock
            ? (this.isProveedorUser() ? this.getVisibleElementos().length : this.state.stock?.cantidadTotal)
            : undefined;
        const productosLabel = this.isProveedorUser()
            ? `producto${cantidadTotal === 1 ? '' : 's'} en consignacion`
            : `producto${cantidadTotal === 1 ? '' : 's'}`;

        return (
            <div
                className="row mb-3 mb-md-4 align-items-center py-3 px-2 rounded admin-page-heading"
                style={PAGE_HEADING_STYLE}
            >
                <div className="col-12 col-md-8">
                    <h4 className="mb-0 text-themecolor color-rojo-megatone fw-bold">Estadisticas</h4>
                    <p className="small text-body-secondary mb-0 mt-1 d-none d-md-block">
                        Consulta de stock y ventas del periodo por proveedor.
                    </p>
                </div>

                <div className="col-12 col-md-4 text-md-end pb-2 pb-md-0 mt-2 mt-md-0">
                    {showFilterToggle ? (
                        <BtnToggleFilter
                            show={this.state.onFilter}
                            handleShow={() => this.setState({ onFilter: !this.state.onFilter })}
                        />
                    ) : null}
                </div>

                {cantidadTotal !== undefined && cantidadTotal !== null && !this.state.loadingStock && (
                    <div className="col-12 mt-2 pt-2 border-top border-light">
                        <span className="admin-metric-pill">
                            <i className="fa fa-cubes" aria-hidden="true" />
                            {formatInteger(cantidadTotal)} {productosLabel}
                        </span>
                    </div>
                )}
            </div>
        );
    }

    renderFiltrosProveedor() {
        return (
            <div className="card card-body filter-card admin-filter-panel mb-3">
                <div className="row form-material align-items-end">
                    <div className="col-lg-3 col-md-6 mb-3 mb-md-0">
                        <label htmlFor="fechaDesdeControlStockProveedor" className="label-oscura">Fecha desde</label>
                        <input
                            id="fechaDesdeControlStockProveedor"
                            type="date"
                            name="fechaDesde"
                            className="form-control"
                            value={this.state.fechaDesde}
                            onChange={this.handleFilterInputChange}
                        />
                    </div>
                    <div className="col-lg-3 col-md-6 mb-3 mb-md-0">
                        <label htmlFor="fechaHastaControlStockProveedor" className="label-oscura">Fecha hasta</label>
                        <input
                            id="fechaHastaControlStockProveedor"
                            type="date"
                            name="fechaHasta"
                            className="form-control"
                            value={this.state.fechaHasta}
                            onChange={this.handleFilterInputChange}
                        />
                    </div>
                    <div className="col-lg-2 col-md-6 mb-3 mb-md-0">
                        <label htmlFor="pageSizeControlStockProveedorFiltro" className="label-oscura">Filas</label>
                        <select
                            id="pageSizeControlStockProveedorFiltro"
                            className="form-control"
                            value={this.state.request.CantidadPagina}
                            onChange={this.handlePageSizeChange}
                        >
                            {PAGE_SIZE_OPTIONS.map((option) => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-lg-4 col-md-12 mb-3 mb-md-0 d-flex align-items-end flex-wrap gap-2">
                        <button
                            type="button"
                            className="btn btn-sm btn-grey"
                            onClick={() => this.fetchProveedorStock()}
                            disabled={this.state.loadingStock || !this.getProveedorCodigo()}
                        >
                            {this.state.loadingStock ? 'Buscando...' : 'Buscar'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-grey"
                            onClick={this.clearFilters}
                            disabled={this.state.loadingStock}
                        >
                            Limpiar fechas
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    renderFiltros() {
        if (this.isProveedorUser()) return null;

        const proveedorOptions = this.mapProveedorOptions();

        return (
            <div className="card card-body filter-card admin-filter-panel mb-3">
                <div className="row form-material align-items-end">
                    <div className="col-lg-4 col-md-6 mb-3 mb-md-0">
                        <label htmlFor="proveedorControlStock" className="label-oscura">Proveedor</label>
                        <Select
                            inputId="proveedorControlStock"
                            name="proveedor"
                            value={this.state.selectedProveedor}
                            options={proveedorOptions}
                            isClearable
                            onChange={this.handleProveedorChange}
                            onMenuOpen={this.mapProveedores}
                            onInputChange={this.onSelectSearchInputProveedores}
                            components={{ DropdownIndicator: () => DropDownArrow }}
                            styles={customStyle}
                            {...filterSelectMenuPortalProps}
                            placeholder="Seleccione proveedor"
                        />
                    </div>
                    <div className="col-lg-2 col-md-6 mb-3 mb-md-0">
                        <label htmlFor="codigoProveedorControlStock" className="label-oscura">Codigo proveedor</label>
                        <input
                            id="codigoProveedorControlStock"
                            type="number"
                            name="codigoProveedor"
                            className="form-control"
                            value={this.state.codigoProveedor}
                            onChange={this.handleFilterInputChange}
                            min={0}
                        />
                    </div>
                    <div className="col-lg-3 col-md-6 mb-3 mb-md-0">
                        <label htmlFor="cuitControlStock" className="label-oscura">CUIT</label>
                        <input
                            id="cuitControlStock"
                            type="text"
                            name="cuit"
                            className="form-control"
                            value={this.state.cuit}
                            onChange={this.handleFilterInputChange}
                            placeholder="Sin guiones"
                        />
                    </div>
                    <div className="col-lg-2 col-md-6 mb-3 mb-md-0">
                        <label htmlFor="productoControlStock" className="label-oscura">Producto</label>
                        <input
                            id="productoControlStock"
                            type="text"
                            name="producto"
                            className="form-control"
                            value={this.state.producto}
                            onChange={this.handleFilterInputChange}
                            placeholder="SKU / descripción"
                        />
                    </div>
                    <div className="col-lg-1 col-md-6 mb-3 mb-md-0">
                        <label htmlFor="depositoStockControlStock" className="label-oscura">Depósito</label>
                        <input
                            id="depositoStockControlStock"
                            type="number"
                            name="depositoStock"
                            className="form-control"
                            value={this.state.depositoStock}
                            onChange={this.handleFilterInputChange}
                            min={0}
                        />
                    </div>
                    <div className="col-lg-2 col-md-6 mb-3 mb-md-0">
                        <label htmlFor="fechaDesdeControlStock" className="label-oscura">Fecha desde</label>
                        <input
                            id="fechaDesdeControlStock"
                            type="date"
                            name="fechaDesde"
                            className="form-control"
                            value={this.state.fechaDesde}
                            onChange={this.handleFilterInputChange}
                        />
                    </div>
                    <div className="col-lg-2 col-md-6 mb-3 mb-md-0">
                        <label htmlFor="fechaHastaControlStock" className="label-oscura">Fecha hasta</label>
                        <input
                            id="fechaHastaControlStock"
                            type="date"
                            name="fechaHasta"
                            className="form-control"
                            value={this.state.fechaHasta}
                            onChange={this.handleFilterInputChange}
                        />
                    </div>
                    <div className="col-lg-1 col-md-6 mb-3 mb-md-0">
                        <label htmlFor="pageSizeControlStock" className="label-oscura">Filas</label>
                        <select
                            id="pageSizeControlStock"
                            className="form-control"
                            value={this.state.request.CantidadPagina}
                            onChange={this.handlePageSizeChange}
                        >
                            {PAGE_SIZE_OPTIONS.map((option) => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="row justify-content-center align-items-center p-10">
                    <div className="col-12 col-md-auto d-flex justify-content-center">
                        <button
                            type="button"
                            className="btn d-inline btn-sm btn-grey"
                            onClick={() => this.fetchStock(1, this.state.request.CantidadPagina)}
                            disabled={this.state.loadingStock || (!this.state.selectedProveedor?.value && !this.state.codigoProveedor && !this.state.cuit)}
                        >
                            {this.state.loadingStock ? 'Buscando...' : 'Buscar'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-grey d-inline ms-2"
                            onClick={this.clearFilters}
                            disabled={this.state.loadingStock}
                        >
                            Limpiar
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    getStockSucursalDisponibleClass = (value) => {
        const n = Number(value);
        if (!Number.isFinite(n)) return '';
        if (n <= 0) return 'stock-sucursales-modal__num--danger';
        if (n <= 5) return 'stock-sucursales-modal__num--warning';
        return 'stock-sucursales-modal__num--ok';
    }

    renderStockSucursalesModal() {
        const modal = this.state.stockSucursalesModal;
        const producto = modal.producto ?? {};
        const sucursales = modal.sucursalesStock ?? [];
        const totalDisponible = sumInteger(sucursales.map((s) => s?.disponibleSucursal));
        const totalReservado = sumInteger(sucursales.map((s) => s?.reservadoCanalDigitalSucursal));
        const totalPendienteFacturar = sumInteger(sucursales.map((s) => s?.pendienteFacturarSucursal));
        const isConsigna = producto?.consigna === true;

        return (
            <Modal
                show={modal.show}
                onHide={this.closeStockSucursalesModal}
                size="xl"
                centered
                backdrop="static"
                dialogClassName="stock-sucursales-modal"
                contentClassName="stock-sucursales-modal__content"
            >
                <Modal.Header closeButton className="stock-sucursales-modal__header">
                    <Modal.Title className="stock-sucursales-modal__title">
                        <span className="stock-sucursales-modal__title-icon" aria-hidden="true">
                            <i className="fa fa-store" />
                        </span>
                        <span className="stock-sucursales-modal__title-text">
                            <span className="stock-sucursales-modal__title-eyebrow">Detalle de inventario</span>
                            Stock por sucursal
                        </span>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="stock-sucursales-modal__body">
                    <div className="stock-sucursales-modal__body-inner">
                        <div className="stock-sucursales-modal__product">
                            <div className="stock-sucursales-modal__product-main">
                                <span className="stock-sucursales-modal__product-icon" aria-hidden="true">
                                    <i className="fa fa-cube" />
                                </span>
                                <div className="stock-sucursales-modal__product-copy">
                                    <span className="stock-sucursales-modal__sku">
                                        SKU {displayValue(producto?.sku)}
                                    </span>
                                    <p className="stock-sucursales-modal__descripcion mb-0">
                                        {displayValue(producto?.descripcion)}
                                    </p>
                                </div>
                            </div>
                            <div className="stock-sucursales-modal__product-badges">
                                {isConsigna && (
                                    <span className="stock-sucursales-modal__badge stock-sucursales-modal__badge--consigna">
                                        <i className="fa fa-check-circle" aria-hidden="true" />
                                        En consignación
                                    </span>
                                )}
                                <span className="stock-sucursales-modal__badge stock-sucursales-modal__badge--stock">
                                    <i className="fa fa-cubes" aria-hidden="true" />
                                    Stock actual {formatInteger(producto?.stockTotal)}
                                </span>
                            </div>
                        </div>

                        {modal.loading ? (
                            <div className="stock-sucursales-modal__loading">
                                <BigSpinner message="Cargando stock por sucursal..." />
                            </div>
                        ) : modal.error ? (
                            <div className="stock-sucursales-modal__alert stock-sucursales-modal__alert--error" role="alert">
                                <i className="fa fa-exclamation-circle" aria-hidden="true" />
                                <span>{modal.error}</span>
                            </div>
                        ) : sucursales.length === 0 ? (
                            <div className="stock-sucursales-modal__alert stock-sucursales-modal__alert--info" role="alert">
                                <i className="fa fa-info-circle" aria-hidden="true" />
                                <span>{modal.mensaje || 'No hay stock por sucursal para mostrar.'}</span>
                            </div>
                        ) : (
                            <>
                                <div className="stock-sucursales-modal__kpis">
                                    <div className="stock-sucursales-modal__kpi">
                                        <span className="stock-sucursales-modal__kpi-label">Sucursales</span>
                                        <span className="stock-sucursales-modal__kpi-value">{formatInteger(sucursales.length)}</span>
                                    </div>
                                    <div className="stock-sucursales-modal__kpi stock-sucursales-modal__kpi--highlight">
                                        <span className="stock-sucursales-modal__kpi-label">Disponible total</span>
                                        <span className={`stock-sucursales-modal__kpi-value ${this.getStockSucursalDisponibleClass(totalDisponible)}`}>
                                            {formatInteger(totalDisponible)}
                                        </span>
                                    </div>
                                    <div className="stock-sucursales-modal__kpi">
                                        <span className="stock-sucursales-modal__kpi-label">Pend. facturar</span>
                                        <span className="stock-sucursales-modal__kpi-value">{formatInteger(totalPendienteFacturar)}</span>
                                    </div>
                                    <div className="stock-sucursales-modal__kpi">
                                        <span className="stock-sucursales-modal__kpi-label">Reservado digital</span>
                                        <span className="stock-sucursales-modal__kpi-value">{formatInteger(totalReservado)}</span>
                                    </div>
                                </div>

                                <div className="stock-sucursales-modal__table-wrap">
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle mb-0 stock-sucursales-modal__table">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Sucursal</th>
                                                    <th scope="col" className="text-end">Nº</th>
                                                    <th scope="col" className="text-end">Depósito venta</th>
                                                    <th scope="col" className="text-end stock-sucursales-modal__th-highlight">Disponible</th>
                                                    <th scope="col" className="text-end">Pend. facturar</th>
                                                    <th scope="col" className="text-end">Pend. ingreso</th>
                                                    <th scope="col" className="text-end">Reservado digital</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sucursales.map((sucursal, index) => (
                                                    <tr key={`${sucursal.numeroSucursal ?? 'sucursal'}-${index}`}>
                                                        <td>
                                                            <span className="stock-sucursales-modal__sucursal-name">
                                                                <i className="fa fa-map-marker-alt text-muted me-1" aria-hidden="true" />
                                                                {displayValue(sucursal.nombreSucursal)}
                                                            </span>
                                                        </td>
                                                        <td className="text-end text-muted">{formatInteger(sucursal.numeroSucursal)}</td>
                                                        <td className="text-end">{formatInteger(sucursal.depositoVenta)}</td>
                                                        <td className={`text-end fw-semibold ${this.getStockSucursalDisponibleClass(sucursal.disponibleSucursal)}`}>
                                                            {formatInteger(sucursal.disponibleSucursal)}
                                                        </td>
                                                        <td className="text-end">{formatInteger(sucursal.pendienteFacturarSucursal)}</td>
                                                        <td className="text-end">{formatInteger(sucursal.pendienteIngresoSucursal)}</td>
                                                        <td className="text-end">{formatInteger(sucursal.reservadoCanalDigitalSucursal)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </Modal.Body>

                <Modal.Footer className="stock-sucursales-modal__footer">
                    <button
                        type="button"
                        className="stock-sucursales-modal__btn-cerrar"
                        onClick={this.closeStockSucursalesModal}
                    >
                        <i className="fa fa-times me-1" aria-hidden="true" />
                        Cerrar
                    </button>
                </Modal.Footer>
            </Modal>
        );
    }

    renderTabla() {
        const elementosRaw = this.getVisibleElementos();
        if (!elementosRaw.length || this.state.loadingStock) return null;

        const showConsignaColumn = this.shouldShowConsignaColumn();
        const paginaActual = this.state.stock?.numeroPagina || 1;
        const totalPaginas = this.state.stock?.totalPaginas || 1;
        const elementosOrdenados = this.sortElementosByStock(elementosRaw);
        const cantidadPagina = Number(this.state.stock?.cantidadPagina ?? this.state.request.CantidadPagina ?? 10);
        const numeroPagina = Number(this.state.stock?.numeroPagina ?? 1);
        const start = Math.max((numeroPagina - 1) * cantidadPagina, 0);
        const end = start + cantidadPagina;
        const elementos = this.isClientPaged()
            ? elementosOrdenados.slice(start, end)
            : elementosOrdenados;
        const stockSortIcon = this.state.stockSort === 'asc'
            ? 'fa fa-sort-amount-up-alt'
            : this.state.stockSort === 'desc'
                ? 'fa fa-sort-amount-down'
                : 'fa fa-sort';

        return (
            <div className="admin-data-table-card mb-3">
                <div className="admin-data-table-card__header">
                    <span className="admin-data-table-card__title">Detalle de productos</span>
                    <span className="admin-metric-pill">
                        Pagina {paginaActual} de {totalPaginas}
                    </span>
                </div>
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead>
                            <tr>
                                <th scope="col">SKU</th>
                                <th scope="col">Descripcion del articulo</th>
                                <th scope="col">Codigo SAP</th>
                                <th scope="col">Subfamilia</th>
                                {showConsignaColumn && <th scope="col">Consignacion</th>}
                                <th scope="col" className="text-end">
                                    <button
                                        type="button"
                                        className="btn btn-link p-0 stock-sort-btn"
                                        onClick={this.toggleStockSort}
                                        title="Ordenar por stock"
                                    >
                                        Stock Actual <i className={stockSortIcon} aria-hidden="true" />
                                    </button>
                                </th>
                                <th scope="col" className="text-end">Unidades vendidas</th>
                            </tr>
                        </thead>
                        <tbody>
                            {elementos.map((item, index) => (
                                (() => {
                                    const isConsignaItem = item?.consigna === true;
                                    const stockNum = Number(item?.stockTotal);
                                    const stockValue = Number.isFinite(stockNum) ? stockNum : null;
                                    const consignaStockClass =
                                        isConsignaItem && stockValue !== null
                                            ? (stockValue <= 0
                                                ? 'stock-consigna-row--sin-stock'
                                                : (stockValue > 0 && stockValue <= 5
                                                    ? 'stock-consigna-row--umbral'
                                                    : ''))
                                            : '';
                                    return (
                                <tr
                                    key={`${item.sku ?? 'sku'}-${item.codigoSap ?? 'sap'}-${item.id ?? index}`}
                                    className={[
                                        isConsignaItem ? 'stock-consigna-row' : '',
                                        consignaStockClass
                                    ].filter(Boolean).join(' ')}
                                >
                                    <td className="fw-semibold">{displayValue(item.sku)}</td>
                                    <td>{displayValue(item.descripcion)}</td>
                                    <td>{displayValue(item.codigoSap)}</td>
                                    <td>
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                            <span>{this.renderSubfamilia(item)}</span>
                                            {isConsignaItem && (
                                                <span className="badge stock-consigna-badge">
                                                    <i className="fa fa-check-circle me-1" aria-hidden="true" />
                                                    En consignación
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    {showConsignaColumn && <td>{this.renderConsigna(item)}</td>}
                                    <td className="text-end">{this.renderStockActualCell(item)}</td>
                                    <td className="text-end">{this.renderUnidadesVendidasCell(item)}</td>
                                </tr>
                                    );
                                })()
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    renderPaginacion() {
        const stock = this.state.stock;
        const elementos = this.getVisibleElementos();
        const totalPaginas = Number(stock?.totalPaginas || 0);
        if (!elementos.length || totalPaginas <= 1) return null;

        return (
            <div className="d-flex justify-content-center mt-3">
                <SafeReactPaginate
                    previousLabel={"Anterior"}
                    nextLabel={"Siguiente"}
                    breakLabel={"..."}
                    breakClassName={"break-me"}
                    pageCount={totalPaginas}
                    marginPagesDisplayed={5}
                    pageRangeDisplayed={5}
                    onPageChange={this.handlePageClick}
                    containerClassName={"pagination"}
                    subContainerClassName={"pages pagination"}
                    activeClassName={"active"}
                    forcePage={(stock.numeroPagina || 1) - 1}
                />
            </div>
        );
    }

    render() {
        if (!this.canAccess()) {
            return (
                <div className="container">
                    <div className="alert alert-warning mt-4">No tenes permisos para acceder a Estadisticas.</div>
                </div>
            );
        }

        const elementos = this.getVisibleElementos();
        const mustSelectProveedor = !this.isProveedorUser() && !this.state.selectedProveedor?.value && !this.state.codigoProveedor && !this.state.cuit && !this.state.loadingStock;
        const emptyResults = !mustSelectProveedor && !this.state.loadingStock && !this.state.error && this.state.stock && elementos.length === 0;

        const showFilterToggle = !this.isProveedorUser();

        return (
            <div className="container">
                {this.renderPageHeading(showFilterToggle)}

                {this.isProveedorUser() && this.renderFiltrosProveedor()}

                <div hidden={!showFilterToggle || !this.state.onFilter}>
                    {this.renderFiltros()}
                </div>

                {this.state.error && (
                    <div className="alert alert-danger" role="alert">
                        {this.state.error}
                    </div>
                )}

                {mustSelectProveedor && (
                    <h5 className="d-flex justify-content-center m-t-40 m-b-40">
                        SELECCIONE UN PROVEEDOR O INGRESE CODIGO / CUIT
                    </h5>
                )}

                <div hidden={!this.state.loadingStock}>
                    <BigSpinner message="Cargando stock de proveedores..." />
                </div>

                {emptyResults && (
                    <h5 className="d-flex justify-content-center m-t-40 m-b-40">
                        NO SE ENCONTRARON RESULTADOS
                    </h5>
                )}

                {!this.state.loadingStock && (
                    <>
                        {this.renderProveedorHeader()}
                        {this.renderResumenVentasYGraficos()}
                        {this.renderTabla()}
                        {this.renderPaginacion()}
                    </>
                )}

                {this.renderStockSucursalesModal()}
            </div>
        );
    }
}

ControlStockProveedores.propTypes = {
    userType: PropTypes.shape({
        isCategory: PropTypes.bool,
        isAdminMod: PropTypes.bool,
        isSellerAdmin: PropTypes.bool,
        isProveedor: PropTypes.bool,
    }),
    tipoUsuario: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    codProv: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    proveedores: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })),
    fetchProveedoresFilter: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
    userType: state.general.userType,
    tipoUsuario: state.general.tipoUsuario,
    codProv: state.general.codProv,
    proveedores: state.sellers.filteredProveedores,
});

export default connect(mapStateToProps, { fetchProveedoresFilter })(ControlStockProveedores);
